import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Stack, CircularProgress, Divider } from '@mui/material';
import { Geofence as GeofenceType } from '../types/geofenceTypes';
import { uploadGeofence, getGeofenceByProject, deleteGeofence, downloadGeofenceKmz } from '../api/geofenceApi';
import DownloadIcon from '@mui/icons-material/Download';
import GeofenceMap from './GeofenceMap';
import { useSnackbar } from '../contexts/SnackbarContext';

interface GeofencePanelProps {
    /** Proyecto sobre el que se define la geocerca. Null cuando aún no se ha guardado el proyecto. */
    projectId?: number | null;
}

/**
 * Panel de geocerca de un proyecto: subir un .kmz para definir el área (polígono) del proyecto y
 * previsualizarla en un mapa. Se usa como pestaña dentro del formulario de proyecto.
 */
const GeofencePanel: React.FC<GeofencePanelProps> = ({ projectId }) => {
    const { showMessage } = useSnackbar();

    const [geofence, setGeofence] = useState<GeofenceType | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);

    useEffect(() => {
        setFile(null);
        setGeofence(null);
        if (projectId == null) {
            return;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const data = await getGeofenceByProject(projectId);
                if (!cancelled) {
                    setGeofence(data);
                }
            } catch (error) {
                if (!cancelled) {
                    showMessage('No se pudo cargar el KMZ del proyecto.', 'error');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFile(event.target.files?.[0] ?? null);
    };

    const handleUpload = async () => {
        if (projectId == null) {
            showMessage('Guarde el proyecto antes de definir su KMZ.', 'warning');
            return;
        }
        if (!file) {
            showMessage('Seleccione un archivo .kmz.', 'warning');
            return;
        }
        setUploading(true);
        try {
            const result = await uploadGeofence(file, projectId);
            setGeofence(result);
            setFile(null);
            showMessage('KMZ cargado correctamente.', 'success');
        } catch (error: any) {
            showMessage(error?.response?.data?.mensaje ?? 'Error al cargar el KMZ.', 'error');
        } finally {
            setUploading(false);
        }
    };

    /**
     * Descarga el .kmz de la geocerca.
     *
     * El backend lo reconstruye desde la geometria guardada, porque el archivo original no se
     * almacena: lo que se descarga son las coordenadas que el sistema esta usando.
     */
    const handleDownload = async () => {
        if (projectId == null) {
            return;
        }
        try {
            setDownloading(true);
            await downloadGeofenceKmz(projectId);
        } catch (error: any) {
            showMessage(error?.response?.data?.mensaje ?? 'No se pudo descargar el KMZ.', 'error');
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (projectId == null) {
            return;
        }
        try {
            await deleteGeofence(projectId);
            setGeofence(null);
            showMessage('KMZ eliminado.', 'success');
        } catch (error: any) {
            showMessage(error?.response?.data?.mensaje ?? 'Error al eliminar el KMZ.', 'error');
        }
    };

    const geometryLabel = (type?: string) => {
        switch (type) {
            case 'POLYGON':
                return 'Polígono (área cerrada)';
            case 'LINESTRING':
                return 'Trazado (línea)';
            case 'POINTS':
                return 'Puntos';
            default:
                return type ?? '—';
        }
    };

    if (projectId == null) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Guarde el proyecto primero para poder definir su KMZ.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
                <input
                    accept=".kmz"
                    style={{ display: 'none' }}
                    id="geofence-kmz-input"
                    type="file"
                    onChange={handleFileChange}
                />
                <label htmlFor="geofence-kmz-input">
                    <Button variant="outlined" component="span">
                        {file ? file.name : 'Seleccionar archivo .kmz'}
                    </Button>
                </label>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploading || !file}
                    startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                    {geofence ? 'Reemplazar KMZ' : 'Cargar KMZ'}
                </Button>
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {geofence ? (
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                            <Typography variant="body2">
                                Geometría: <strong>{geometryLabel(geofence.geometryType)}</strong>
                            </Typography>
                            {geofence.sourceFilename && (
                                <>
                                    <Divider orientation="vertical" flexItem />
                                    <Typography variant="body2" color="text.secondary">
                                        {geofence.sourceFilename}
                                    </Typography>
                                </>
                            )}
                            <Button
                                size="small"
                                startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                                onClick={handleDownload}
                                disabled={downloading}
                            >
                                Descargar
                            </Button>
                            <Button size="small" color="error" onClick={handleDelete}>
                                Eliminar
                            </Button>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Este proyecto aún no tiene KMZ. Suba un archivo .kmz para definirlo.
                        </Typography>
                    )}
                    <GeofenceMap geojson={geofence?.geojson} />
                </>
            )}
        </Box>
    );
};

export default GeofencePanel;
